// src/pages/admin/TowerAdminBillingConfig.jsx
import React, { useEffect, useState } from "react";
import AdminSidebar from "../../components/AdminSidebar";
import {
  getBillingConfig,
  setBillingConfig,
  getBillingBasisOptions,
  generateCharges,
} from "../../services/billingAPI";
import { useConfirm } from "../../components/ConfirmDialog.jsx";
import "./admin.css";
import "./TowerAdminBillingConfig.css";

export default function TowerAdminBillingConfig() {
  const confirm = useConfirm();
  const [config, setConfig] = useState(null);
  const [billingBasisOptions, setBillingBasisOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [generatingCharges, setGeneratingCharges] = useState(false);

  const towerStr = localStorage.getItem("adminTower"); // "Tower 8"
  const tower = towerStr ? parseInt(towerStr.split(" ")[1]) : null; // Gets "8"

  // Debug logging
  useEffect(() => {
    console.log("TowerAdminBillingConfig mounted");
    console.log("Tower from localStorage:", localStorage.getItem("adminTower"));
    console.log("AdminRole from localStorage:", localStorage.getItem("adminRole"));
    console.log("Token exists:", !!localStorage.getItem("token"));
  }, []);

  // Form state
  const [formData, setFormData] = useState({
    costPerSqft: 0,
    billingBasis: "carpet_with_terrace",
    discounts: {
      monthly: 0,
      quarterly: 0,
      annual: 0,
    },
    lateFeePercentage: 0,
    gracePeriodDays: 7,
    billingStartDay: 1,
    dueDateDays: 5,
  });

  // Load config on mount
  useEffect(() => {
    if (!tower) {
      setError("Tower information not found. Please log in as Tower Admin.");
      setLoading(false);
      return;
    }

    loadConfig();
    loadBillingBasisOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tower]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError("");

      console.log("Loading config for tower:", tower);

      const data = await getBillingConfig(tower);

      console.log("Config loaded:", data);

      if (data) {
        setConfig(data);
        setFormData({
          costPerSqft: data.costPerSqft || 0,
          billingBasis: data.billingBasis || "carpet_with_terrace",
          discounts: data.discounts || { monthly: 0, quarterly: 0, annual: 0 },
          lateFeePercentage: data.lateFeePercentage || 0,
          gracePeriodDays: data.gracePeriodDays || 7,
          billingStartDay: data.billingStartDay || 1,
          dueDateDays: data.dueDateDays || 5,
        });
      } else {
        setError("No billing configuration found for this tower. Create one first.");
      }

      setLoading(false);
    } catch (err) {
      console.error("Error loading config:", err);
      setError("Failed to load billing configuration: " + err.message);
      setLoading(false);
    }
  };

  const loadBillingBasisOptions = async () => {
    try {
      const data = await getBillingBasisOptions();
      setBillingBasisOptions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load billing basis options:", err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.includes("discounts")) {
      const discountType = name.split(".")[1];
      setFormData({
        ...formData,
        discounts: {
          ...formData.discounts,
          [discountType]: parseFloat(value) || 0,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: name === "costPerSqft" || name.includes("Percentage") || name.includes("Days")
          ? parseFloat(value) || 0
          : value,
      });
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();

    try {
      setSaveLoading(true);
      setError("");
      setSuccess("");

      const response = await setBillingConfig(tower, formData);

      if (response.config) {
        setConfig(response.config);
        setSuccess("Billing configuration saved successfully!");
        setTimeout(() => setSuccess(""), 3000);
      }

      setSaveLoading(false);
    } catch (err) {
      setError("Failed to save configuration: " + err.message);
      setSaveLoading(false);
    }
  };

  const handleGenerateCharges = async (frequency) => {
    const ok = await confirm({
      title: "Generate charges?",
      message: `This creates ${frequency} charges for every approved resident in this tower. It can't be undone from here.`,
      confirmLabel: "Generate charges",
    });
    if (!ok) {
      return;
    }

    try {
      setGeneratingCharges(true);
      setError("");
      setSuccess("");

      const response = await generateCharges(tower, frequency);

      setSuccess(
        `✓ Charges generated! ${response.generatedCount} residents, ${response.failedCount} failed.`
      );
      setTimeout(() => setSuccess(""), 5000);

      setGeneratingCharges(false);
    } catch (err) {
      setError("Failed to generate charges: " + err.message);
      setGeneratingCharges(false);
    }
  };

  // Check if tower is missing
  if (!tower) {
    return (
      <div className="admin-layout">
        <AdminSidebar />
        <div className="admin-content">
          <div className="billing-config-container">
            <div className="alert alert-error">
              ⚠️ Tower information not found. Please log in as Tower Admin again.
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-layout">
        <AdminSidebar />
        <div className="admin-content">
          <div className="billing-config-container">
            <div className="loading">Loading billing configuration...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <div className="billing-config-container">
          <div className="config-header">
            <h1>⚙️ Billing Configuration</h1>
            <p>Tower {tower} - Manage maintenance charges settings</p>
          </div>

          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <div className="config-main-grid">
          <form onSubmit={handleSaveConfig} className="config-form">
        {/* Section 1: Basic Rates */}
        <div className="form-section">
          <h2>📊 Basic Rates</h2>

          <div className="form-group">
            <label htmlFor="costPerSqft">Cost per Square Foot (₹)</label>
            <input
              type="number"
              id="costPerSqft"
              name="costPerSqft"
              value={formData.costPerSqft}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              placeholder="Enter cost per sqft"
            />
            <small>Amount charged per sqft for maintenance</small>
          </div>

          <div className="form-group">
            <label htmlFor="billingBasis">Billing Basis</label>
            <select
              id="billingBasis"
              name="billingBasis"
              value={formData.billingBasis}
              onChange={handleInputChange}
            >
              {billingBasisOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} - {option.description}
                </option>
              ))}
            </select>
            <small>How to calculate area for billing</small>
          </div>
        </div>

        {/* Section 2: Discounts */}
        <div className="form-section">
          <h2>🎁 Discounts by Frequency</h2>

          <div className="discount-grid">
            <div className="form-group">
              <label htmlFor="discount-monthly">Monthly Discount (%)</label>
              <input
                type="number"
                id="discount-monthly"
                name="discounts.monthly"
                value={formData.discounts.monthly}
                onChange={handleInputChange}
                step="0.1"
                min="0"
                max="100"
                placeholder="0"
              />
              <small>Discount for monthly payments</small>
            </div>

            <div className="form-group">
              <label htmlFor="discount-quarterly">Quarterly Discount (%)</label>
              <input
                type="number"
                id="discount-quarterly"
                name="discounts.quarterly"
                value={formData.discounts.quarterly}
                onChange={handleInputChange}
                step="0.1"
                min="0"
                max="100"
                placeholder="0"
              />
              <small>Discount for quarterly payments</small>
            </div>

            <div className="form-group">
              <label htmlFor="discount-annual">Annual Discount (%)</label>
              <input
                type="number"
                id="discount-annual"
                name="discounts.annual"
                value={formData.discounts.annual}
                onChange={handleInputChange}
                step="0.1"
                min="0"
                max="100"
                placeholder="0"
              />
              <small>Discount for annual payments</small>
            </div>
          </div>
        </div>

        {/* Section 3: Late Fees & Grace Period */}
        <div className="form-section">
          <h2>⏰ Late Fees & Grace Period</h2>

          <div className="form-group">
            <label htmlFor="lateFeePercentage">Late Fee Percentage (%)</label>
            <input
              type="number"
              id="lateFeePercentage"
              name="lateFeePercentage"
              value={formData.lateFeePercentage}
              onChange={handleInputChange}
              step="0.1"
              min="0"
              max="100"
              placeholder="0"
            />
            <small>Percentage charge applied after grace period</small>
          </div>

          <div className="form-group">
            <label htmlFor="gracePeriodDays">Grace Period (Days)</label>
            <input
              type="number"
              id="gracePeriodDays"
              name="gracePeriodDays"
              value={formData.gracePeriodDays}
              onChange={handleInputChange}
              step="1"
              min="0"
              max="90"
              placeholder="7"
            />
            <small>Days after due date before late fees apply</small>
          </div>
        </div>

        {/* Section 4: Billing Schedule */}
        <div className="form-section">
          <h2>📅 Billing Schedule</h2>

          <div className="form-group">
            <label htmlFor="billingStartDay">Billing Start Day (of month)</label>
            <input
              type="number"
              id="billingStartDay"
              name="billingStartDay"
              value={formData.billingStartDay}
              onChange={handleInputChange}
              step="1"
              min="1"
              max="28"
              placeholder="1"
            />
            <small>Day of month when billing period starts</small>
          </div>

          <div className="form-group">
            <label htmlFor="dueDateDays">Due Date (Days from issue)</label>
            <input
              type="number"
              id="dueDateDays"
              name="dueDateDays"
              value={formData.dueDateDays}
              onChange={handleInputChange}
              step="1"
              min="1"
              max="60"
              placeholder="5"
            />
            <small>Number of days from issue to due date</small>
          </div>
        </div>

        {/* Save Button */}
        <button type="submit" className="btn-save" disabled={saveLoading}>
          {saveLoading ? "Saving..." : "💾 Save Configuration"}
        </button>
      </form>

      <div className="side-panel">
      {/* Generate Charges Section */}
      <div className="charges-section">
        <h2>🔄 Generate Charges</h2>
        <p>Create charges for all approved residents in Tower {tower}</p>

        <div className="charge-buttons">
          <button
            className="btn-generate monthly"
            onClick={() => handleGenerateCharges("monthly")}
            disabled={generatingCharges}
          >
            {generatingCharges ? "Generating..." : "Generate Monthly"}
          </button>
          <button
            className="btn-generate quarterly"
            onClick={() => handleGenerateCharges("quarterly")}
            disabled={generatingCharges}
          >
            {generatingCharges ? "Generating..." : "Generate Quarterly"}
          </button>
          <button
            className="btn-generate annual"
            onClick={() => handleGenerateCharges("annual")}
            disabled={generatingCharges}
          >
            {generatingCharges ? "Generating..." : "Generate Annual"}
          </button>
        </div>

        <div className="info-box">
          <p>
            💡 <strong>Tip:</strong> Run charge generation once per billing period. 
            It will create charges for all residents based on current configuration.
          </p>
        </div>
      </div>

      {/* Current Configuration Info */}
      {config && (
        <div className="config-info">
          <h3>📋 Current Configuration Summary</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="label">Cost per Sqft:</span>
              <span className="value">₹{config.costPerSqft}</span>
            </div>
            <div className="info-item">
              <span className="label">Billing Basis:</span>
              <span className="value">{config.billingBasis}</span>
            </div>
            <div className="info-item">
              <span className="label">Quarterly Discount:</span>
              <span className="value">{config.discounts?.quarterly || 0}%</span>
            </div>
            <div className="info-item">
              <span className="label">Annual Discount:</span>
              <span className="value">{config.discounts?.annual || 0}%</span>
            </div>
            <div className="info-item">
              <span className="label">Late Fee:</span>
              <span className="value">{config.lateFeePercentage || 0}%</span>
            </div>
            <div className="info-item">
              <span className="label">Grace Period:</span>
              <span className="value">{config.gracePeriodDays} days</span>
            </div>
          </div>
          <small>Last updated: {new Date(config.updatedAt).toLocaleDateString()}</small>
        </div>
      )}
      </div>
          </div>
        </div>
      </div>
    </div>
  );
}